import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { endOfMonth, format, parseISO } from 'date-fns';

import { useCallbackTickets } from '~/data/hooks/useCallbacks';
import type { CallbackTicket } from '~/data/schemas/callbacks';
import { BUTTON_SX } from '~/theme';
import { buildUrlForTickets } from '~/utils/urlBuilder';

const TYPE_LABELS: Record<CallbackTicket['entryType'], string> = {
  regular: 'Regular Callback',
  emergency: 'Emergency/Entrapment',
  deferred: 'Deferred Callback',
};

const isoDay = (value: Date) => format(value, 'yyyy-MM-dd');

function CallbackRow({ ticket }: { ticket: CallbackTicket }) {
  return (
    // Stacked on a phone: beside the ticket number the chips took the width the number
    // and date needed, so the number wrapped to a sliver and the chip ran off the edge.
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      gap={{ xs: 1, sm: 2 }}
      sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider' }}
    >
      <Stack gap={0.25}>
        <Link
          href={buildUrlForTickets({ number: ticket.number })}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          underline="hover"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          {ticket.number}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Typography variant="caption" color="text.secondary">
            {format(parseISO(ticket.calledOn), 'd MMM yyyy')}
          </Typography>
          {ticket.problemType ? (
            <Typography variant="caption" color="text.secondary">
              {ticket.problemType}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        {ticket.equipmentFailure ? <Chip size="small" label="Equipment related" /> : null}
        <Chip
          size="small"
          label={TYPE_LABELS[ticket.entryType]}
          color={ticket.entryType === 'emergency' ? 'error' : 'default'}
          variant={ticket.entryType === 'emergency' ? 'filled' : 'outlined'}
        />
      </Stack>
    </Stack>
  );
}

/** The callbacks behind one cell of the grid. */
export function CallbackMonthModal({
  carId,
  carName,
  monthStart,
  onClose,
}: {
  carId: string;
  carName: string;
  monthStart: string;
  onClose: () => void;
}) {
  const month = parseISO(monthStart);
  const { tickets, isLoading, error } = useCallbackTickets({
    car: carId,
    start_date: monthStart,
    end_date: isoDay(endOfMonth(month)),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {carName}
        <Typography variant="body2" color="text.secondary">
          {format(month, 'MMMM yyyy')}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error">Those callbacks could not be loaded.</Alert> : null}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}
        {!isLoading && !error && tickets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nobody called this elevator in during {format(month, 'MMMM yyyy')}.
          </Typography>
        ) : null}
        {tickets.map((ticket) => (
          <CallbackRow key={ticket.id} ticket={ticket} />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={BUTTON_SX}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
